import { cn } from "@/lib/cn.js";
import { addDays, durationMinutes, isoDate, startOfWeek } from "@/lib/format.js";
import type { Appointment, Customer, Resource, Service, SyncConflict, TenantClosure } from "@/lib/types.js";
import { AppointmentBlock } from "./appointment-block.js";
import { DAY_START_HOUR, MINUTES_PER_ROW, ROW_HEIGHT_PX, TOTAL_ROWS } from "./constants.js";
import { pickDayClosure, tenantDayLabel } from "./closures-overlay.js";

/**
 * Week view (PRD calendar-ux-parity REQ-CUX-003): 7 columns × time-gutter
 * rows for a SINGLE selected resource over a 7-day window. PRD OQ-CUX-2:
 * one resource at a time (the toolbar `ResourceSelect` picks). Cross-
 * resource overlap is surfaced by switching resource — no all-resources
 * week view in v1.5.
 *
 * Closed-day overlay is per-column (REQ-CUX-012); a closed column gets a
 * diagonal-stripe background and a reason chip in its header.
 */
export function CalendarWeekGrid({
  day,
  tenantTz,
  resource,
  appointments,
  services,
  customers,
  conflicts,
  closures,
  onResize,
  onClickEmpty,
  onClickAppointment
}: {
  day: Date;
  tenantTz: string;
  resource: Resource;
  appointments: Appointment[];
  services: Service[];
  customers: Customer[];
  conflicts: SyncConflict[];
  closures: TenantClosure[];
  onResize: (appointmentId: string, newEndsAt: string) => Promise<void>;
  onClickEmpty: (startsAt: string, resourceId: string) => void;
  onClickAppointment: (appointment: Appointment) => void;
}): React.JSX.Element {
  const start = startOfWeek(day.toISOString());
  const days: Date[] = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
  const today = isoDate(new Date());
  const serviceById = new Map(services.map((s) => [s.id, s]));
  const customerById = new Map(customers.map((c) => [c.id, c]));
  const rows = Array.from({ length: TOTAL_ROWS }, (_, i) => i);

  return (
    <div data-testid="calendar-week-grid" className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="overflow-auto scrollbar-thin">
        <div
          className="grid"
          style={{ gridTemplateColumns: `56px repeat(7, minmax(140px, 1fr))` }}
        >
          {/* Header row */}
          <div className="sticky top-0 z-10 border-b bg-card/90 backdrop-blur" />
          {days.map((d) => {
            const iso = isoDate(d);
            const isToday = iso === today;
            const dayLabel = tenantDayLabel(d, tenantTz);
            const dayClosure = pickDayClosure(closures, dayLabel, tenantTz);
            return (
              <div
                key={iso}
                data-testid={`week-day-header-${iso}`}
                className={cn(
                  "sticky top-0 z-10 flex flex-col gap-0.5 border-b border-l bg-card/90 px-3 py-2.5 backdrop-blur",
                  isToday && "bg-primary/5"
                )}
              >
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {d.toLocaleDateString(undefined, { weekday: "short" })}
                </span>
                <span className={cn("text-sm font-semibold", isToday && "text-primary")}>
                  {d.getDate()}
                </span>
                {dayClosure && (
                  <span
                    className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-warning/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground"
                  >
                    Closed · {dayClosure.reason}
                  </span>
                )}
              </div>
            );
          })}

          {/* Time gutter (first column) */}
          <div className="relative border-r">
            {rows.map((row) => {
              const minutes = row * MINUTES_PER_ROW;
              const showLabel = minutes % 60 === 0;
              return (
                <div
                  key={row}
                  style={{ height: ROW_HEIGHT_PX }}
                  className={cn("relative", showLabel && "border-t")}
                >
                  {showLabel && (
                    <span className="absolute -top-2 right-2 bg-card px-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {formatHourLabel(DAY_START_HOUR + Math.floor(minutes / 60))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Day columns */}
          {days.map((d) => {
            const iso = isoDate(d);
            const dayLabel = tenantDayLabel(d, tenantTz);
            const dayClosure = pickDayClosure(closures, dayLabel, tenantTz);
            return (
              <DayColumn
                key={iso}
                day={d}
                resource={resource}
                appointments={appointments.filter((a) => {
                  if (a.primary_resource_id !== resource.id) return false;
                  return tenantDayLabel(a.starts_at, tenantTz) === dayLabel;
                })}
                services={serviceById}
                customers={customerById}
                conflicts={conflicts}
                dayClosure={dayClosure}
                onResize={onResize}
                onClickEmpty={onClickEmpty}
                onClickAppointment={onClickAppointment}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DayColumn({
  day,
  resource,
  appointments,
  services,
  customers,
  conflicts,
  dayClosure,
  onResize,
  onClickEmpty,
  onClickAppointment
}: {
  day: Date;
  resource: Resource;
  appointments: Appointment[];
  services: Map<string, Service>;
  customers: Map<string, Customer>;
  conflicts: SyncConflict[];
  dayClosure: { id: string; reason: string } | null;
  onResize: (appointmentId: string, newEndsAt: string) => Promise<void>;
  onClickEmpty: (startsAt: string, resourceId: string) => void;
  onClickAppointment: (a: Appointment) => void;
}): React.JSX.Element {
  const iso = isoDate(day);
  const dayStart = new Date(day);
  dayStart.setHours(DAY_START_HOUR, 0, 0, 0);

  function isoForRow(row: number): string {
    const d = new Date(dayStart);
    d.setMinutes(d.getMinutes() + row * MINUTES_PER_ROW);
    return d.toISOString();
  }

  return (
    <div
      data-testid={`week-day-col-${iso}`}
      data-closed={dayClosure ? "true" : "false"}
      className={cn("relative border-l bg-background/40", dayClosure && "bg-muted/40")}
      style={{ height: TOTAL_ROWS * ROW_HEIGHT_PX }}
    >
      {dayClosure && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(160, 160, 160, 0.18) 0, rgba(160, 160, 160, 0.18) 6px, transparent 6px, transparent 12px)"
            }}
          />
          <span
            data-testid={`week-day-closure-chip-${iso}`}
            className="pointer-events-none absolute left-1.5 top-1.5 z-[2] inline-flex items-center gap-1 rounded-md bg-warning/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground"
          >
            Closed · {dayClosure.reason}
          </span>
        </>
      )}
      {Array.from({ length: TOTAL_ROWS }, (_, i) => (
        <div
          key={i}
          className={cn(
            "absolute left-0 right-0 cursor-pointer transition-colors duration-100 hover:bg-primary/5 motion-reduce:transition-none",
            i % 4 === 0 && "border-t border-border",
            i % 4 === 2 && "border-t border-border/40"
          )}
          style={{ top: i * ROW_HEIGHT_PX, height: ROW_HEIGHT_PX }}
          onClick={() => onClickEmpty(isoForRow(i), resource.id)}
        />
      ))}
      {appointments.map((appointment) => {
        const top = minutesFromDayStart(appointment.starts_at, dayStart) / MINUTES_PER_ROW;
        const height = Math.max(2, durationMinutes(appointment.starts_at, appointment.ends_at) / MINUTES_PER_ROW);
        if (top < 0 || top > TOTAL_ROWS) return null;
        const service = services.get(appointment.service_id);
        const customer = customers.get(appointment.customer_id);
        return (
          <AppointmentBlock
            key={appointment.id}
            top={top * ROW_HEIGHT_PX}
            height={height * ROW_HEIGHT_PX}
            appointment={appointment}
            service={service}
            customer={customer}
            conflicts={conflicts}
            resizable
            onClick={() => onClickAppointment(appointment)}
            onResize={onResize}
          />
        );
      })}
    </div>
  );
}

function minutesFromDayStart(iso: string, dayStart: Date): number {
  return Math.floor((Date.parse(iso) - dayStart.getTime()) / 60000);
}

function formatHourLabel(hour: number): string {
  const am = hour < 12 || hour === 24;
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${am ? "a" : "p"}`;
}

// Re-export addDays so consumers don't have to deep-import.
export { addDays };
